import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  SearchPageProperties,
  FilterDefinition,
  SearchPageModule,
  FilterOption,
  FilterTypeNames,
  PagedResponse,
  SortOption,
  RecentlyViewedItem,
  SearchAndFilterRequest,
} from "@/types/search-page";

import {
  CertificationSearchResultModel,
  SearchAndFilterCertificationsRequest,
} from "@/types/certifications";
import {
  SearchAndFilterDisbursementsRequest,
  DisbursementSearchResultModel,
} from "@/types/disbursements";

import { LocationQueryValue } from "vue-router";
import functions from "@/use/functions.js";
import { $api } from "@/services/api1";
import { isAdminUser } from "@/use/useIsCreditUnionUser";
import { useRoute, useRouter } from "vue-router";

export const useSearchPageStore = defineStore("searchPage", () => {
  const route = useRoute();
  const router = useRouter();

  // State
  const searchPageState = ref<any>({
    loading: true,
    error: false,
    entity: "" as string,
    pageName: "" as string,
    annualReviewErrorMessage: "" as string,
    applications: [] as any[] | string,
    certifications: [] as CertificationSearchResultModel[] | string,
    disbursements: [] as DisbursementSearchResultModel[] | string,
    loans: [] as any[],
    dataDropdown: [],
    dropdownData: false,
    filterDisplay: false,
    filterOrder: 0,
    filters: [],
    expandedFilters: [],
    collapsedFilters: [],
    layout: "boxes",
    modal: false,
    recentlyViewed: false as boolean | any[],
    filterModal: {
      active: false,
      filterCategoryId: 0,
    },
    search: {
      text: null as string | LocationQueryValue[] | null,
      type: "",
      typing: null as string | LocationQueryValue[] | null,
      timestamp: 0,
      active: false,
      focus: false,
      error: false,
      dropdown: false,
    },
    searchResults: {
      error: false,
      errorMessage: "",
      data: [],
    },
    query: {
      pageNumber: false as boolean | number,
      pageTotal: 0 as number,
      totalCount: 0 as number,
      perPage: 30,
      paginationEnabled: false,
      perPageOptions: [30, 60, 90],
      sortOptions: [],
    },
    filterDefinitions: [] as FilterDefinition[],
  });

  const showAdvancedSearchModal = ref<boolean>(false);

  const advancedSearchEnabled = computed<boolean>(() => {
    if (searchPageState.value?.pageName?.toLowerCase() === "applications") {
      return true;
    }
    return false;
  });

  const enableSearchOnCLUID = computed<boolean>(() => {
    if (searchPageState.value?.pageName?.toLowerCase() === "loans") {
      return true;
    }
    return false;
  });

  const enableSearchOnEmail = computed<boolean>(() => {
    if (searchPageState.value?.pageName?.toLowerCase() === "applications") {
      return true;
    }
    return false;
  });

  const searchCLUID = computed<string>(() => {
    if (enableSearchOnCLUID.value) {
      return " CLUID,";
    }
    return "";
  });

  const searchEmail = computed<string>(() => {
    if (enableSearchOnEmail.value) {
      return " Email,";
    }
    return "";
  });

  // Getters
  const searchResultsData = computed(() => {
    if (isCertificationsPage()) {
      return searchPageState.value.certifications;
    }
    if (isDisbursementsPage()) {
      return searchPageState.value.disbursements;
    }
    return "Loading...";
  });

  // Actions
  function $reset() {
    // Resets the state
    searchPageState.value = {
      loading: true,
      error: false,
      entity: "",
      pageName: "",
      applications: [],
      certifications: [],
      disbursements: [],
      loans: [],
      dataDropdown: [],
      dropdownData: false,
      filterDisplay: false,
      filterOrder: 0,
      filters: [],
      expandedFilters: [],
      collapsedFilters: [],
      layout: "boxes",
      modal: false,
      recentlyViewed: false,
      filterModal: {
        active: false,
        filterCategoryId: 0,
      },
      search: {
        text: null as string | LocationQueryValue[] | null,
        type: "",
        typing: null as string | LocationQueryValue[] | null,
        timestamp: 0,
        active: false,
        focus: false,
        error: false,
        dropdown: false,
      },
      searchResults: {
        error: false,
        errorMessage: "Error Loading Results",
        data: [],
      },
      query: {
        pageNumber: false as boolean | number,
        pageTotal: 0 as number,
        totalCount: 0 as number,
        perPage: 30,
        paginationEnabled: false,
        perPageOptions: [30, 60, 90],
        sortOptions: [],
        expandedFilters: [],
        collapsedFilters: [],
      },
      results: {
        error: false,
        errorMessage: "Error Loading Results",
        data: [],
      },
      filterDefinitions: [],
    };
  }

  function getRouteIdentifier(pageName: string, result: any) {
    if (isCertificationsPage()) {
      return result.id;
    } else if (isDisbursementsPage()) {
      return result.certificationId || result.id;
    } else if (pageName === "loans") {
      return result.id;
    }

    return result.referenceId;
  }

  function sectionResultsExceedMaxVisible(filterDefinition: FilterDefinition) {
    return (
      filterDefinition.options.length >
      (filterDefinition.maxVisibleOptions || filterDefinition.options.length)
    );
  }

  function isCertificationsPage() {
    return route.name === "Certifications";
  }

  function isDisbursementsPage() {
    return route.name === "Disbursements";
  }

  function getSearchAndFilterRequest(
    searchText: string | null,
  ): SearchAndFilterRequest {
    return {
      search: searchText,
      filters: searchPageState.value.filters,
      page: {
        index: searchPageState.value.query.pageNumber
          ? searchPageState.value.query.pageNumber
          : 1,
        size: searchPageState.value.query.perPage ?? 30,
      },
      sortOptions: getSortOptionsFromUrl() || ([] as SortOption[]),
    };
  }

  async function getDisbursements() {
    searchPageState.value.searchResults.error = false;

    const currentSearchRequest: SearchAndFilterRequest =
      getSearchAndFilterRequest(searchPageState.value.search.text);

    const response = await $api.disbursements.searchAndFilterDisbursements(
      currentSearchRequest,
    );

    if (response.items) {
      if (response.items.length <= 0) {
        // check if any values exist for the default pagination
        const searchResults =
          await $api.disbursements.searchAndFilterDisbursements({
            ...currentSearchRequest,
            page: {
              index: 1,
              size: searchPageState.value.query.perPage ?? 30,
            },
          });

        if (searchResults.items.length > 0) {
          response.items = searchResults.items;
          response.page = searchResults.page;
          response.totalCount = searchResults.totalCount;
        }
      }

      searchPageState.value.disbursements = response.items;
      searchPageState.value.searchResults.data = response.items;

      setPaginationState(response);
    } else {
      console.error("Error retrieving data:", response);
      searchPageState.value.loading = false;
      searchPageState.value.searchResults.error = true;
      searchPageState.value.searchResults.errorMessage =
        "Error Loading Results";
    }

    searchPageState.value.loading = false;
    return response;
  }

  async function getCertifications() {
    searchPageState.value.searchResults.error = false;

    const currentSearchRequest: SearchAndFilterRequest =
      getSearchAndFilterRequest(searchPageState.value.search.text);

    const response = await $api.certifications.searchAndFilterCertifications(
      currentSearchRequest,
    );

    if (response?.items) {
      if (response.items.length <= 0) {
        // check if any values exist for the default pagination
        const searchResults =
          await $api.certifications.searchAndFilterCertifications({
            ...currentSearchRequest,
            page: {
              index: 1,
              size: searchPageState.value.query.perPage ?? 30,
            },
          });

        if (searchResults.items.length > 0) {
          response.items = searchResults.items;
          response.page = searchResults.page;
          response.totalCount = searchResults.totalCount;
        }
      }

      searchPageState.value.certifications = response.items;
      searchPageState.value.searchResults.data = response.items;

      setPaginationState(response);
    } else {
      console.error("Error retrieving data:", response);
      searchPageState.value.loading = false;
      searchPageState.value.searchResults.error = true;
      searchPageState.value.searchResults.errorMessage =
        "Error Loading Results";
    }

    searchPageState.value.loading = false;
    return response;
  }

  function $dispose() {
    //Pinia's `$dispose()` method is used to remove a store instance, not reset its state.
  }

  function openFilterModal(filterCategoryId: number) {
    searchPageState.value.filterModal.active = true;
    searchPageState.value.filterModal.filterCategoryId = filterCategoryId;
  }

  function closeFilterModal() {
    searchPageState.value.filterModal.active = false;
    searchPageState.value.filterModal.filterCategoryId = 0;
  }

  //////////// PAGINATION FUNCTIONS ///////////
  function isValidPageSize(pageSize: number | LocationQueryValue) {
    return searchPageState.value.query.perPageOptions.includes(pageSize);
  }

  function setPaginationState(response: PagedResponse) {
    searchPageState.value.query.pageNumber = response.page.index ?? 1;
    searchPageState.value.query.totalCount = response.totalCount ?? 0;
    searchPageState.value.query.pageTotal =
      response.totalCount > 0
        ? Math.ceil(response.totalCount / response.page.size)
        : 1;
    searchPageState.value.query.perPage =
      response.page.size ?? searchPageState.value.query.perPage;

    router.push({
      query: {
        ...route.query,
        pageNumber: response.page.index,
        pageSize: response.page.size,
      },
    });
  }

  function handlePageNumberClick(pageNumber: number) {
    setPaginationPageNumber(pageNumber);
    router.push({ query: { ...route.query, pageNumber: pageNumber } });
  }

  function handlePageSizeChange(pageSize: number) {
    if (isValidPageSize(pageSize)) {
      // if the total pages would be 1 or less after changing page size, reset page to 1
      if (Math.ceil(searchPageState.value.query.totalCount / pageSize) === 1) {
        setPaginationPageNumber(1);
      }

      setPaginationPageSize(pageSize);
      router.push({
        query: {
          ...route.query,
          pageSize: pageSize,
          pageNumber:
            Math.ceil(searchPageState.value.query.totalCount / pageSize) === 1
              ? 1
              : searchPageState.value.query.pageNumber,
        },
      });
    }
  }

  function paginationPrevious() {
    handlePageNumberClick(searchPageState.value.query.pageNumber - 1);
  }

  function paginationNext() {
    handlePageNumberClick(searchPageState.value.query.pageNumber + 1);
  }

  function setPaginationPageSize(pageSize: number) {
    searchPageState.value.query.perPage = pageSize;
  }

  function setPaginationPageNumber(page: number) {
    searchPageState.value.query.pageNumber = page;
  }

  function setCurrentEntity(entity: string) {
    searchPageState.value.entity = entity;
  }

  //////////// FILTER FUNCTIONS ///////////
  function filteredSectionResults(
    section: FilterDefinition,
  ): Array<FilterOption> {
    return section.options.filter((item) => {
      const itemNameMatches = item.name
        ?.toLowerCase()
        .includes(section.search?.toLowerCase());
      const isVisible =
        !item.internalOnly || (isAdminUser() && item.internalOnly === true);

      return isVisible && (!section.search || itemNameMatches);
    });
  }

  async function getFilters(pageName: string) {
    setCurrentEntity(pageName);

    const filtersResponse = await $api.filters.getFilters(
      searchPageState.value.entity,
    );

    if (filtersResponse.filters) {
      setSearchPageFilters(filtersResponse.filters);

      searchPageState.value.query.expandedFilters = filtersResponse.filters
        .filter((f: FilterDefinition) => !f.collapsed)
        .map((fd: FilterDefinition) => fd.name);

      searchPageState.value.query.collapsedFilters = filtersResponse.filters
        .filter((f: FilterDefinition) => f.collapsed)
        .map((fd: FilterDefinition) => fd.name);
    }
  }

  function setSearchPageFilters(filterDefinitions: FilterDefinition[]) {
    searchPageState.value.filters = filterDefinitions;
  }

  function getSelectedFilters(filterCategoryId: number, filterType: string) {
    const selectedFilter = searchPageState.value.filters.find(
      (f: FilterDefinition) => f.filterCategoryId === filterCategoryId,
    );

    if (
      filterType === FilterTypeNames.Radio ||
      filterType === FilterTypeNames.Select ||
      filterType === "dropdown"
    ) {
      const selectedFilterOption = selectedFilter?.options?.find(
        (fo: FilterOption) => fo.checked,
      )?.id;

      return selectedFilterOption || null;
    }

    return selectedFilter?.options
      ?.filter((fo: FilterOption) => fo.checked)
      ?.map((o: FilterOption) => o.id);
  }

  function selectedFilters(output?: any) {
    ///////////////////////////////////////////////////////
    // description: returns an array of selected filters //
    ///////////////////////////////////////////////////////

    const data = [] as any[];
    let dataString = "" as string;

    // for each filter section that is not a radio button
    searchPageState.value.filters
      .filter(
        (section) =>
          section.filterTypeName != FilterTypeNames.Radio &&
          section.filterTypeName != FilterTypeNames.Select &&
          section.filterTypeName != "dropdown",
      )
      .forEach((section: FilterDefinition) => {
        // for each of the fields inside that filter section
        section.options.forEach((field) => {
          // if the field has children and is not checked
          if (field.children && !field.checked) {
            // for each of the children
            field.children.forEach((child) => {
              // if the child is checked
              if (child.checked) {
                // add the section to the object
                child["section"] = section.description || section.label;

                // push the child to the data array
                data.push(child);
              }
            });

            // else if the field is checked
          } else if (field.checked) {
            // add the section to the object
            field["section"] = section.description || section.label;

            // push the field to the data array
            data.push(field);
          }

          if (field.children) {
            // set the field to "checked" if every child is checked (the .every() method returns true or false)
            field.checked = field.children.every((child) => child.checked);
          }
        });
      });

    // sort the filter buttons by the order they were clicked
    data.sort((a, b) => {
      return a.filterOrder - b.filterOrder;
    });

    // option to output the array as text
    if (output == "text") {
      // convert the array to a string to return selected filters as text
      dataString = data
        .map((field) => {
          return `"${field.name}"`;
        })
        .join(" or ");
    }

    return output == "text" ? dataString : data;
  }

  async function searchAndFilterCertifications(searchText: string | null) {
    const certificationsResponse =
      await $api.certifications.searchAndFilterCertifications(
        getSearchAndFilterRequest(searchText),
      );

    return certificationsResponse;
  }

  async function searchAndFilterDisbursements(searchText: string | null) {
    const disbursementsResponse =
      await $api.disbursements.searchAndFilterDisbursements(
        getSearchAndFilterRequest(searchText),
      );

    return disbursementsResponse;
  }

  function handleSearchBarKeyup(event: any) {
    if (event.key === "Enter") {
      return;
    }
    if (searchPageState.value.search.typing.length >= 3) {
      searchDropdown(searchPageState.value.search.typing);
    } else {
      searchPageState.value.dataDropdown = [];
    }
  }

  function focusSearchbox() {
    searchPageState.value.search.focus = true;
  }

  function blurSearchbox() {
    // waits 0.2 seconds before closing the searchbox so the dropdown click works without issues
    setTimeout(() => {
      searchPageState.value.search.focus = false;
    }, 200);
  }

  function getSortOptionsFromUrl() {
    if (searchPageState.value.layout === "table") {
      const keys = Object.keys(route.query);

      if (keys.length > 0) {
        if (
          keys.includes("resultSortKey") &&
          keys.includes("resultSortOrder") &&
          (route.query["resultSortKey"] || "").length > 0
        ) {
          return [
            {
              property: route.query["resultSortKey"]?.toString(),
              descending: Number(route.query["resultSortOrder"]) === -1,
            } as SortOption,
          ];
        }
      }

      return [];
    }
  }

  function changeLayout(layout: string) {
    searchPageState.value.layout = layout;

    router.push({
      query: {
        ...route.query,
        Layout: layout,
      },
      hash: route.hash,
    });
  }

  function updateSectionSearchText(
    searchText: string,
    filterDefinition: FilterDefinition,
  ) {
    const section = searchPageState.value.filters.find(
      (f: FilterDefinition) => f.name === filterDefinition.name,
    );

    if (section) {
      section.search = searchText;
    }
  }

  function isValidJSONString(
    str: string | LocationQueryValue | LocationQueryValue[] | null,
  ) {
    try {
      if (str === null) {
        return false;
      } else {
        JSON.parse(str.toString());
      }

      return true; // Parsing successful, it's valid JSON
    } catch (e) {
      return false; // Parsing failed, it's not valid JSON
    }
  }

  function checkFilterParam(
    filters: FilterDefinition[],
    param: string,
    name: string,
  ) {
    const filterOptions = searchPageState.value.filters?.[0]?.options;
    filterOptions?.sort((a: FilterDefinition, b: FilterDefinition) =>
      a.name.localeCompare(b.name),
    );

    const filter = filters.find(
      (f: FilterDefinition) => f.queryParameter === param,
    );
    const result = filter?.options?.find((f: FilterOption) => f.name === name);

    if (result) {
      result.checked = true;
    }
  }

  async function handleParametersFromUrl() {
    // ensure filters state is populated
    if (searchPageState.value.filters?.length === 0) {
      console.log("no filters");
      return;
    }

    if (Object.keys(route.query)?.length > 0) {
      Object.entries(route.query).forEach(([key, value]) => {
        const validJson = isValidJSONString(value);

        if (typeof value === "string" && validJson) {
          const queryValues = JSON.parse(value);
          // if the parameter value is an array when parsed
          if (Array.isArray(queryValues)) {
            if (key == "SearchTerms" || key === "names") {
              searchPageState.value.search.active = true;
              searchPageState.value.search.text = queryValues.join(" ");
              searchPageState.value.search.typing = queryValues.join(" ");
            } else if (
              key === "CollapsedFilters" ||
              key === "ExpandedFilters"
            ) {
              queryValues.forEach((queryValue: any) => {
                const selectedFilter = searchPageState.value.filters.find(
                  (f: FilterDefinition) =>
                    f.filterCategoryId == parseInt(queryValue),
                );

                if (selectedFilter) {
                  if (key === "CollapsedFilters") {
                    selectedFilter.collapsed = true;
                  }
                  if (key === "ExpandedFilters") {
                    selectedFilter.collapsed = false;
                  }
                }
              });

              // if none in query param, handle defaults
            }
            // for each of the values in the array
            else {
              queryValues.forEach((queryValue: any) => {
                const selectedFilter = searchPageState.value.filters.find(
                  (f: FilterDefinition) => f.queryParameter == key,
                );

                if (selectedFilter) {
                  selectedFilter.options.forEach((option: FilterOption) => {
                    // singular select
                    if (
                      selectedFilter.filterTypeName === FilterTypeNames.Radio ||
                      selectedFilter.filterTypeName ===
                        FilterTypeNames.Select ||
                      selectedFilter.filterTypeName === "dropdown"
                    ) {
                      // for each of the radio buttons or option in the dropdown
                      selectedFilter.options.forEach((item: FilterOption) => {
                        // uncheck all the radio buttons
                        item.checked = false;
                      });

                      selectedFilter.options.filter(
                        (f: FilterOption) =>
                          f.id == queryValue || f.id === parseInt(queryValue),
                      )[0].checked = true;
                    } else if (
                      selectedFilter.filterTypeName === FilterTypeNames.Checkbox
                    ) {
                      // for each of the options that is checked
                      selectedFilter.options.forEach((item: FilterOption) => {
                        // uncheck all the radio buttons
                        if (Number(item.id) === Number(queryValue)) {
                          // set the child checked to true
                          item.checked = true;
                        }
                      });

                      // set the filter definition to "checked" if every child is checked (the .every() method returns true or false)
                      selectedFilter.checked = selectedFilter.options.every(
                        (option: FilterOption) => option.checked,
                      );
                    } else if (Number(option.id) === Number(queryValue)) {
                      option.checked = true;
                    } else {
                      // handle else
                    }
                  });
                }
              });
            }
          }

          // if parameter value not an array when parsed
          else {
            if (key == "SearchTerm") {
              searchPageState.value.search.active = true;
              searchPageState.value.search.text = value || "";
              searchPageState.value.search.typing = value || "";
            } else if (key == "PageNumber" || key === "pageNumber") {
              searchPageState.value.query.pageNumber = Number(value);
            } else if (key === "PageSize" || key === "pageSize") {
              // check that page size is a valid 30,60,90
              if (isValidPageSize(Number(value))) {
                searchPageState.value.query.perPage = Number(value);
              } else {
                searchPageState.value.query.perPage = 30;
              }
            }
          }
        } else {
          if (key === "Layout") {
            searchPageState.value.layout = value || "boxes";
          }
          if (key == "SearchTerm") {
            searchPageState.value.search.active = true;
            searchPageState.value.search.text = value || "";
            searchPageState.value.search.typing = value || "";
          }
        }
      });
    }
  }

  async function getParametersFromUrl(pageName: string) {
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // description: checks which parameters are present in the page url and selects filters based on those parameters //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    // clear all existing filters on the page
    clearAllFilters(pageName);

    if (pageName == "Applications") {
      checkFilterParam(searchPageState.value.filters, "ProductIds", "All");
    } else if (pageName == "Loans") {
      checkFilterParam(searchPageState.value.filters, "ProductTypeIds", "All");
      checkFilterParam(
        searchPageState.value.filters,
        "AnnualReviewYear",
        "Choose a Year",
      );
    } else if (pageName == "Certifications") {
      checkFilterParam(searchPageState.value.filters, "ProductIds", "All");
    } else if (pageName === "Disbursements") {
      checkFilterParam(searchPageState.value.filters, "ProductIds", "All");
    }

    handleParametersFromUrl();
  }

  async function searchEntities() {
    ///////////////////////////////////////////////////////////////////////////////////
    // description: searches applications/loans/certifications and displays the results on the page //
    ///////////////////////////////////////////////////////////////////////////////////
    searchPageState.value.loading = true;

    try {
      if (isCertificationsPage()) {
        const certificationsResponse = await searchAndFilterCertifications(
          searchPageState.value.search.text,
        );

        if (certificationsResponse.items && certificationsResponse.page) {
          searchPageState.value.certifications = certificationsResponse.items;
          searchPageState.value.searchResults.data =
            certificationsResponse.items;
          setPaginationState(certificationsResponse);
        }
      } else if (isDisbursementsPage()) {
        const disbursementsResponse = await searchAndFilterDisbursements(
          searchPageState.value.search.text,
        );

        if (disbursementsResponse.items && disbursementsResponse.page) {
          searchPageState.value.disbursements = disbursementsResponse.items;
          searchPageState.value.searchResults.data =
            disbursementsResponse.items;
          setPaginationState(disbursementsResponse);
        }
      } else {
        searchApplications(searchPageState.value.pageName);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      searchPageState.value.searchResults.errorMessage =
        "Error Loading Results";
    }

    searchPageState.value.loading = false;
  }

  async function searchApplications(pageName: string) {
    searchPageState.value.data = "Loading...";
    searchPageState.value.loading = true;
    try {
      const response = await functions[`get${pageName}`](
        searchPageState.value.search.text,
        searchPageState.value.filters,
        searchPageState.value.query.pageNumber,
      );

      if (response.data) {
        searchPageState.value.data =
          pageName === "Loans" ? response.data.items : response.data.data;

        searchPageState.value.query = {
          pageNumber: response.data.pageNumber || response.data.page.index,
          pageTotal: Math.ceil(
            response.data.totalCount / response.data.page.size,
          ),
          totalCount: response.data.totalCount,
        };
      } else {
        // no matches found
        searchPageState.value.data = [];
        searchPageState.value.query = {
          pageNumber: false,
          pageTotal: 0,
          totalCount: 0,
        };
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      searchPageState.value.data = "Error Loading Results";
    }
    searchPageState.value.loading = false;
  }

  async function searchDropdown(searchText: any) {
    ///////////////////////////////////////////////////////////////////////////////////////////////
    // description: displays search results in a dropdown as the user is typing in the searchbox //
    ///////////////////////////////////////////////////////////////////////////////////////////////

    // clean the search text
    searchText = functions.cleanSearchText(searchText);
    searchPageState.value.search.typing = searchText;
    searchPageState.value.search.timestamp = Date.now();

    if (isCertificationsPage()) {
      const certificationsResponse = await searchAndFilterCertifications(
        searchText,
      );

      if (certificationsResponse) {
        searchPageState.value.dataDropdown = certificationsResponse.items;
      }
    } else if (isDisbursementsPage()) {
      const disbursementsResponse = await searchAndFilterDisbursements(
        searchText,
      );

      if (disbursementsResponse) {
        searchPageState.value.dataDropdown = disbursementsResponse.items;
      }
    } else {
      // handle other pages here
    }
  }

  function allowSearchBySSN() {
    if (isDisbursementsPage()) {
      return false;
    }

    return true;
  }

  function getSearchParameters(
    searchText: any,
    searchFilters,
    pageNumber: number | LocationQueryValue,
    pageSize?: number | LocationQueryValue,
  ) {
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // description: this takes the search text and all active filters and outputs parameters for the url and post body //
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    const parameters = {};
    let searchType = null as null | string;

    // if the user is searching
    if (searchText) {
      // make sure the user has typed at least 3 alphanumeric characters, and less than 200
      if (
        searchText?.replace(/[^0-9a-z]/gi, "").length >= 3 &&
        searchText?.length <= 200
      ) {
        // if the user is searching by name [the search text will contain a letter as the first character]
        if (searchText.charAt(0).match(/[a-z]/i)) {
          // set the search type to name
          searchType = "Name";
          parameters["SearchType"] = 1;
          parameters["SearchTerms"] = searchText.split(" ");
          parameters["names"] = searchText.split(" ");

          // if the user is searching by ssn [the search text will validate with the isValidSSN function]
        } else if (functions.isValidSSN(searchText) && allowSearchBySSN()) {
          // set the search type to ssn
          searchType = "SSN";
          parameters["SearchType"] = 3;
          parameters["SearchTerm"] = searchText;
          parameters["ssn"] = searchText;

          // if the user is searching by application id [the search text will contain an amount of numbers not equal to 9]
        } else {
          // set the search type to application id
          searchType = "Application ID";
          parameters["SearchType"] = 2;
          parameters["SearchTerm"] = searchText;
          parameters["referenceId"] = searchText;
        }
      } else {
        return { parameters: {}, searchType: "Unknown" };
      }
    }

    // if search filters are set
    if (searchFilters) {
      // loop through each of the filters
      searchFilters.forEach((section) => {
        // handle non search / filter query parameters
        let key = null as null | string;
        if (section.collapsed) {
          key = "CollapsedFilters";
        } else if (!section.collapsed) {
          key = "ExpandedFilters";
        }

        if (key) {
          parameters[key] = parameters[key] || [];

          // append the value to the parameter
          parameters[key].push(section.filterCategoryId);
        }

        // for each of the fields inside that filter
        section.options.forEach((field) => {
          // if the field has children
          if (field.children) {
            // for each of the children which are checked
            field.children
              .filter((child) => child.checked)
              .forEach((child) => {
                // check if the parameter exists, otherwise create it as a blank array
                parameters[section.queryParameter] =
                  parameters[section.queryParameter] || [];

                // append the value to the parameter
                parameters[section.queryParameter].push(child.id);
              });

            // else if the field is checked
          } else if (field.checked && field.id != "skip") {
            // check if the parameter exists, otherwise create it as a blank array
            parameters[section.queryParameter] =
              parameters[section.queryParameter] || [];

            // append the value to the parameter
            parameters[section.queryParameter].push(field.id);
          }
        });
      });
    }

    // if there is a pagenumber
    if (pageNumber && Number(pageNumber) > 1) {
      parameters["pageNumber"] = pageNumber;
    }

    // if there is a pageSize
    if (pageSize && isValidPageSize(pageSize)) {
      parameters["pageSize"] = pageSize;
    }
    // if query values that are not filters already exist
    if (Object.keys(route.query)?.length > 0) {
      const validQueryKeys = ["Layout"];

      Object.keys(route.query).forEach((key: string) => {
        if (validQueryKeys.includes(key)) {
          if (typeof route.query[key] === "string") {
            parameters[key] = "";
            parameters[key] = route.query[key];
          }
        }
      });
    }

    // return the search parameters
    return {
      parameters: parameters || {},
      searchType: searchType || "Unknown",
    };
  }

  async function triggerSearch(
    pageName: string,
    searchText?: string | LocationQueryValue[] | null,
    pageNumber?: number,
  ) {
    /////////////////////////////////////////////////////////////////////////////////////////
    // description: adds search parameters to the page url to trigger searchApplications() //
    /////////////////////////////////////////////////////////////////////////////////////////

    if (searchText) {
      searchText = functions.cleanSearchText(searchText);
      searchPageState.value.search.typing = searchText || "";
      searchPageState.value.search.text = searchText || "";
      searchPageState.value.search.active = true;
    } else if (
      searchPageState.value.search.active &&
      searchPageState.value.search.text
    ) {
      searchText = functions.cleanSearchText(searchPageState.value.search.text);
      searchPageState.value.search.typing = searchText || "";
      searchPageState.value.search.text = searchText || "";
    }

    if (pageNumber) {
      searchPageState.value.query.pageNumber = pageNumber;
    }

    // if the user is searching by ssn
    if (functions.isValidSSN(searchText)) {
      searchEntities();
    } else {
      const filters = JSON.parse(JSON.stringify(searchPageState.value.filters));

      const response = getSearchParameters(
        searchText,
        filters,
        searchPageState.value.query.pageNumber,
        searchPageState.value.query.perPage,
      );

      if (response) {
        // convert the object to url parameters
        const urlParameters = functions.objectToUrlString(response.parameters);

        // redirect to the search url to perform the search
        router.push("?" + urlParameters);
      } else {
        functions.openModal("Error Getting Search Parameters");
        console.log("Error: triggerSearch()->getSearchParameters()");
      }
    }
  }

  function clearSearchbox() {
    ///////////////////////////////////////////////////////////////
    // description: clears the search box and deactivates search //
    ///////////////////////////////////////////////////////////////

    // reset the search text
    searchPageState.value.search.text = "";
    searchPageState.value.search.typing = "";

    // if the search is active (and not just a dropdown)
    if (searchPageState.value.search.active) {
      // deactivate the search
      searchPageState.value.search.active = false;
    }
  }

  function clearFilter(item: FilterOption) {
    ////////////////////////////////////////////////////////
    // description: clears a single filter on the sidebar //
    ////////////////////////////////////////////////////////

    // uncheck the item
    item.checked = false;

    // if the item has children
    if (item.children) {
      // for each of the children
      item.children.forEach((child) => {
        // uncheck all the child fields
        child.checked = false;
      });
    }

    triggerSearch(
      searchPageState.value.entity,
      searchPageState.value.search.text,
      searchPageState.value.query.pageNumber,
    );
  }

  function clearAllFiltersAndSearch(pageName?: string) {
    ///////////////////////////////////////////////////////////////////////////////////////
    // description: redirects to the search page without any query parameters in the url //
    ///////////////////////////////////////////////////////////////////////////////////////

    router.push("");

    clearAllFilters(pageName);
  }

  function clearAllFilters(pageName?: string) {
    const entity = pageName ?? searchPageState.value.entity;
    ////////////////////////////////////////////////////////
    // description: clears all the filters on the sidebar //
    ////////////////////////////////////////////////////////

    // for each filter section
    searchPageState.value.filters?.forEach((section: FilterDefinition) => {
      // for each of the fields inside that filter section
      section.options?.forEach((field: FilterOption) => {
        // uncheck all the fields
        field.checked = false;

        if (field?.children) {
          // for each of the children
          field.children?.forEach((child) => {
            // uncheck the child fields
            child.checked = false;
          });
        }
      });
    });

    clearSearchbox();
  }

  function toggleFilterSection(
    action: string,
    filterDefinition?: FilterDefinition,
  ) {
    if (action !== "ignore" && filterDefinition) {
      if (action === "expand") {
        expandFilterSection(filterDefinition);
      } else {
        collapseFilterSection(filterDefinition);
      }
    }

    const parameters = {};

    // loop through each of the filters
    searchPageState.value.filters.forEach((f: FilterDefinition) => {
      let key = null as null | string;

      if (f.collapsed) {
        key = "CollapsedFilters";
      } else {
        key = "ExpandedFilters";
      }

      if (key) {
        parameters[key] = parameters[key] || [];

        // append the value to the parameter
        parameters[key].push(f.filterCategoryId);
      }
    });

    searchPageState.value.query.collapsedFilters =
      parameters["CollapsedFilters"];
    searchPageState.value.query.expandedFilters = parameters["ExpandedFilters"];

    const updatedQueryParams = {
      ...route.query,
      CollapsedFilters: parameters["CollapsedFilters"],
      ExpandedFilters: parameters["ExpandedFilters"],
    };

    const updatedUrlParameters =
      functions.objectToUrlString(updatedQueryParams);

    router.push("?" + updatedUrlParameters);
  }

  function collapseFilterSection(filterDefinition: FilterDefinition) {
    const section = searchPageState.value.filters.find(
      (f: FilterDefinition) => f.description === filterDefinition.description,
    );

    if (section) {
      section.collapsed = true;
    }
  }

  function expandFilterSection(filterDefinition: FilterDefinition) {
    const section = searchPageState.value.filters.find(
      (f: FilterDefinition) => f.description === filterDefinition.description,
    );

    if (section) {
      section.collapsed = false;
    }
  }

  function selectFilter(event?: any, input?: any, parent?: any) {
    ////////////////////////////////////////////////////
    // description: executes when a filter is clicked //
    ////////////////////////////////////////////////////

    if (
      parent?.filterTypeName === FilterTypeNames.Radio ||
      parent?.filterTypeName === FilterTypeNames.Select ||
      parent?.filterTypeName === "dropdown"
    ) {
      // for each of the radio buttons or options in the dropdown
      parent.options.forEach((item) => {
        // uncheck all the radio buttons
        item.checked = false;
      });

      // check the radio button which was just clicked
      input.checked = true;
    } else {
      // update the value on the clicked checkbox
      input.checked = event.target.checked;

      input.filterOrder = searchPageState.value.filterOrder++;

      // if the filter has children
      if (input.children) {
        // loop through each of the children of the parent
        input.children.forEach((item) => {
          item.filterOrder = searchPageState.value.filterOrder++;

          // set the child to match the parent
          item.checked = event.target.checked;
        });
      }

      // if the filter has a parent
      if (parent && parent.children) {
        // set the parent to "checked" if every child is checked (the .every() method returns true or false)
        parent.checked = parent.children.every((child) => child.checked);
      }
    }

    if (!validateAnnualReviewSelections(input, parent)) {
      return;
    }

    triggerSearch(
      searchPageState.value.entity,
      searchPageState.value.search.text,
      searchPageState.value.query.pageNumber || 1,
    );
  }

  function validateAnnualReviewSelections(
    input: FilterDefinition,
    parent: FilterDefinition,
  ) {
    const FILTER_ANNUAL_REVIEW_STATUS = "AnnualReviewStatusIds";
    const FILTER_ANNUAL_REVIEW_YEAR = "AnnualReviewYear";

    if (
      (input?.queryParameter ?? "") != FILTER_ANNUAL_REVIEW_STATUS &&
      (parent?.queryParameter ?? "") != FILTER_ANNUAL_REVIEW_YEAR
    ) {
      return true;
    }

    const selectedStatus = searchPageState.value.filters
      .find(
        (f: FilterDefinition) =>
          f.queryParameter === FILTER_ANNUAL_REVIEW_STATUS,
      )
      ?.results.some((item: FilterOption) => item.checked);

    const selectedYear = searchPageState.value.filters
      .find(
        (f: FilterDefinition) => f.queryParameter === FILTER_ANNUAL_REVIEW_YEAR,
      )
      ?.results.some(
        (item: FilterOption) => item.checked && item.id !== "skip",
      );

    if (selectedStatus && !selectedYear) {
      searchPageState.value.annualReviewErrorMessage =
        "Please select an Annual Review Year.";
      return false;
    } else if (!selectedStatus && selectedYear) {
      searchPageState.value.annualReviewErrorMessage =
        "Please select an Annual Review Status.";
      return false;
    }

    searchPageState.value.annualReviewErrorMessage = "";

    return true;
  }

  function getRecentlyViewedPages() {
    // check cache for recently viewed by page
    let recentlyViewed = [] as any[] | null;
    const cacheKey = "cache.RecentlyViewedPages";

    if (localStorage.getItem(cacheKey)) {
      // retrieve the data
      recentlyViewed = JSON.parse(localStorage.getItem(cacheKey) || "");

      if (Array.isArray(recentlyViewed)) {
        recentlyViewed = recentlyViewed.filter(
          (r: RecentlyViewedItem) => r.page === route.name,
        );

        searchPageState.value.recentlyViewed = recentlyViewed;
      }
    }
  }

  function handleSearchResultClick(
    result: CertificationSearchResultModel | DisbursementSearchResultModel,
    pageName?: string | null,
  ) {
    const page = pageName
      ? pageName
      : route.name || searchPageState.value.pageName;

    const id = getRouteIdentifier(page, result);

    router.push(`${page}/${id}`);

    functions.logPageView({
      id: id,
      page: page,
      category: route.name,
      timestamp: Date.now(),
      borrowerName: result.borrowerName,
      borrowerInitials: result.borrowerInitials,
      lenderName: result.lenderName,
    });
  }

  return {
    showAdvancedSearchModal,
    searchResultsData,
    searchPageState,
    enableSearchOnCLUID,
		enableSearchOnEmail,
		searchCLUID,
		searchEmail,
    advancedSearchEnabled,
    setCurrentEntity,
    setPaginationPageSize,
    setPaginationPageNumber,
    getFilters,
    changeLayout,
    blurSearchbox,
    focusSearchbox,
    handleSearchBarKeyup,
    clearSearchbox,
    triggerSearch,
    selectFilter,
    clearFilter,
    clearAllFilters,
    clearAllFiltersAndSearch,
    updateSectionSearchText,
    filteredSectionResults,
    toggleFilterSection,
    searchAndFilterCertifications,
    getCertifications,
    selectedFilters,
    isCertificationsPage,
    getRouteIdentifier,
    $reset,
    checkFilterParam,
    getParametersFromUrl,
    searchEntities,
    handlePageNumberClick,
    handlePageSizeChange,
    paginationPrevious,
    paginationNext,
    openFilterModal,
    closeFilterModal,
    sectionResultsExceedMaxVisible,
    getDisbursements,
    validateAnnualReviewSelections,
    getRecentlyViewedPages,
    handleSearchResultClick,
    isValidJSONString,
  };
});
